import React, { useEffect, useState, useRef } from 'react';
import { InstructionsContainer, H1 } from './Pages-styles';
import AdminContentPanel from '../components/AdminContentPanel';
import { usePageContext } from '../hooks/PageContext';
import { SEOHead } from '../components/SEO/SEOHead';

interface IDistribution {
  name: string;
  icon: string;
  description: string;
  size: string;
  downloadLink: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'https://sd.smetaforte.ru/api';

const DistPage: React.FC = () => {
  const { data, actions, userRole } = usePageContext();
  const canEdit = ['admin', 'superadmin'].includes(userRole || '');
  const [items, setItems] = useState<IDistribution[]>([
    {
      name: 'manifest.json',
      icon: 'Folder',
      description: 'Манифест приложения содержит настройки и метаданные для запуска программы.',
      size: 'Загрузка...',
      downloadLink: '/Dists/manifest.json',
    },
    {
      name: 'SmetaFORTE.msi',
      icon: 'Folder',
      description: 'Установочный файл программы для Windows. Содержит все необходимые библиотеки и зависимости.',
      size: 'Загрузка...',
      downloadLink: '/Dists/SmetaFORTE.msi',
    },
  ]);

  const [listKey, setListKey] = useState(0); // Используем key для перерисовки
  const resizeTimeout = useRef<number | null>(null);

  // Получаем размер файла
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getFileSize = async (url: string): Promise<string> => {
    try {
      const response = await fetch(url, { method: 'HEAD', cache: 'no-cache' });
      if (response.ok) {
        const length = response.headers.get('Content-Length');
        if (length) {
          const sizeInBytes = parseInt(length, 10);
          return formatBytes(sizeInBytes);
        }
      }
    } catch (e) {
      console.error(`Не удалось получить размер файла: ${url}`, e);
    }
    return 'Неизвестно';
  };

  // Форматируем байты
  const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    else if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
  };

  // Загружаем размеры
  useEffect(() => {
    const fetchSizes = async () => {
      const updatedItems = [...items];

      for (let i = 0; i < updatedItems.length; i++) {
        const dist = updatedItems[i];
        const size = await getFileSize(dist.downloadLink);
        updatedItems[i] = { ...dist, size };
      }

      setItems(updatedItems);
    };

    fetchSizes();
  }, [getFileSize, items]);

  // Подписываемся на ресайз окна
  useEffect(() => {
    const handleResize = () => {
      if (resizeTimeout.current) {
        window.clearTimeout(resizeTimeout.current);
      }

      resizeTimeout.current = window.setTimeout(() => {
        setListKey(prev => prev + 1); // Изменяем key для перерисовки
      }, 200);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <InstructionsContainer>
          <SEOHead
            title="СметаFORTE - Главная"
            description="СметаFORTE - профессиональный модуль для сметного дела. Сопоставительные ведомости, управление проектами, автоматизация расчетов."
            keywords={['смета', 'СВОР', 'Ведомость', 'ведомость', 'свор', 'работы', 'сметы', 'сопоставительная ведомость объёмов работ', 'цэс', 'свор быстро', 'автоматически', 'объёмы работ', 'программый модуль', '', '', '', 'строительство', 'автоматизация', 'ГРАНД-Смета', 'СметаFORTE']}
          />
      <H1>Файлы для загрузки:</H1>

      <AdminContentPanel
        activeContent={data?.activeContent || { type: null, data: null }}
        loading={data?.loading || false}
        canEdit={canEdit}
        availableNotes={actions?.availableNotes || []}
        onSaveArticle={actions?.setAsDist}
        onDeleteArticle={actions?.deleteArticle}
        onSaveVideo={actions?.saveVideo}
        onClearVideo={actions?.clearVideo}
        apiURL={API_URL}
        contentMode="dynamic" 
      />
    </InstructionsContainer>
  );
};

export default DistPage;